import { NgStyle } from "@angular/common";
import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { ChartComponent } from "@components/chart/chart.component";
import { LabelHoverPlugin } from "@components/chart/plugins/hoverPlugin";
import { IconComponent } from "@components/icon/icon.component";
import { TranslateService } from "@ngx-translate/core";
import { ChartConfiguration } from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import { HandelssortimentsgruppDTO } from "~app/common/models/common/HandelssortimentsgruppDTO";
import { IHandelssortimentsgrupp } from "~app/features/planera/model/IHandelssortimentsgrupp";
import { ProduktionsmalPlanMode } from "~app/features/planera/model/ProduktionsmalPlanMode";
import { IProduktionsmalPlanResult } from "../produktionsmal-plan.service";
import { ChartMode, ProduktionsmalPlanChartService } from "./produktionsmal-plan-chart.service";

@Component({
    selector: "rask-produktionsmal-plan-chart",
    templateUrl: "./produktionsmal-plan-chart.component.html",
    styleUrls: ["./produktionsmal-plan-chart.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        ChartComponent,
        NgStyle,
        IconComponent
    ]
})
export class ProduktionsmalPlanChartComponent implements OnChanges {
    chartConfiguration: ChartConfiguration;
    chartHeight: string;
    chartMode: ChartMode;
    @Input() handelssortimentsgrupper: HandelssortimentsgruppDTO[];
    negativeGoals: string[] = [];
    @Input() produktionsmalPlan: IProduktionsmalPlanResult;
    @Input() produktionsmalPlanMode: ProduktionsmalPlanMode;
    @Input() selectedHandelssortimentsgruppKod: string;

    private readonly produktionsmalPlanChartService = inject(ProduktionsmalPlanChartService);
    private readonly translate = inject(TranslateService);
    private readonly warningGlyph = "\u26A0";

    readonly getCommaListedNegativeGoals = () => this.negativeGoals.join(", ");

    initChart(data, plugins, scales, produktionsmalPlanMode) {
        this.chartHeight = `${this.produktionsmalPlanChartService.getChartHeight(data.labels.length, produktionsmalPlanMode)}px`;
        this.chartConfiguration = this.produktionsmalPlanChartService.getChartConfiguration(data, plugins, scales, produktionsmalPlanMode, this.chartMode);
    }

    readonly isChartModeHandelssortimentsgruppEnabled = () => this.chartMode === ChartMode.Handelssortimentsgrupp;

    readonly isHandelssortimentGruppPeriodsattStapel = (mouseElement: any, planeringsstatus: string) =>
        mouseElement.index === 0 &&
        planeringsstatus === this.translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_PERIODSATT");

    readonly isHandelssortimentStapel = (mouseElement: any) => mouseElement.index > 0;

    readonly isProduktionsmalPlanDetailModeEnabled = () => this.produktionsmalPlanMode === ProduktionsmalPlanMode.detail;

    readonly isProduktionsmalPlanTopModeEnabled = () => this.produktionsmalPlanMode === ProduktionsmalPlanMode.top;

    loadHandelssortimentsgrupp(handelssortimentsgrupp: IHandelssortimentsgrupp) {
        this.chartMode = ChartMode.Handelssortimentsgrupp;
        const data = this.produktionsmalPlanChartService.getDataForHandelssortimentsgrupp(handelssortimentsgrupp, this.isProduktionsmalPlanTopModeEnabled());
        const secondRowLineWidth = (this.isProduktionsmalPlanTopModeEnabled() ? 3 : 1);
        this.negativeGoals = data.lessThanZeroLabels;
        this.markNegativeGoals(data.labels);
        const scales = {
            y:
                {
                    type: "category",
                    min: 0,
                    ticks: {
                        beginAtZero: true,
                        font: {
                            size: 14
                        }
                    },
                    grid: {
                        drawBorder: false,
                        color: [
                            "rgba(0, 0, 0, 0.1)",
                            "rgba(0, 0, 0, 0.1)"
                        ],
                        lineWidth: [
                            1,
                            secondRowLineWidth
                        ]
                    }
                },
            x:
                {
                    type: "linear",
                    position: "top",
                    ticks: {
                        beginAtZero: true,
                        font: {
                            size: 14
                        }
                    }
                }
        };

        this.initChart(data, [], scales, this.produktionsmalPlanMode);
    }

    loadHandelssortimentsgrupper() {
        this.chartMode = ChartMode.Handelssortimentsgrupper;
        const data = this.produktionsmalPlanChartService.getDataForHandelssortimentsgrupper(this.produktionsmalPlan.handelssortimentsgrupper);

        this.negativeGoals = data.lessThanZeroLabels;
        this.markNegativeGoals(data.labels);

        this.produktionsmalPlanChartService.markNegativeGoals(data.labels, this.negativeGoals);

        const plugins = [new LabelHoverPlugin()];
        const scales = {
            y: {
                type: "category",
                stacked: true,
                min: 0,
                ticks: {
                    beginAtZero: true,
                    font: {
                        size: 14
                    },
                    color: "#237ee3"
                }
            },
            x: {
                type: "linear",
                position: "top",
                ticks: {
                    beginAtZero: true,
                    font: {
                        size: 14
                    }
                }
            }
        };

        this.initChart(data, plugins, scales, this.produktionsmalPlanMode);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.produktionsmalPlan && this.selectedHandelssortimentsgruppKod) {
            const selectedHandelssortimentsgrupp =
                this.produktionsmalPlanChartService.getHandelssortimentByKod(this.selectedHandelssortimentsgruppKod, this.produktionsmalPlan.handelssortimentsgrupper);

            if (selectedHandelssortimentsgrupp) {
                this.loadHandelssortimentsgrupp(selectedHandelssortimentsgrupp);
            }
        } else if (this.produktionsmalPlan) {
            this.loadHandelssortimentsgrupper();
        }
    }

    onChartClick(clickObj) {
        const chart = clickObj.chart;
        const mousePoint = getRelativePosition(clickObj.event, chart);
        const mouseElement = chart.getElementsAtEventForMode(clickObj.event, "nearest", { intersect: true }, false)[0];

        if ((this.isChartModeHandelssortimentsgruppEnabled() || this.isProduktionsmalPlanDetailModeEnabled()) && mouseElement) {
            const planeringsstatus: string = chart.data.datasets[mouseElement.datasetIndex].label;
            const hsNamn: string = chart.data.labels[mouseElement.index].replace(this.warningGlyph, "").trim();

            if (this.isHandelssortimentStapel(mouseElement) && planeringsstatus !== this.translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_MAL")) {
                const id = this.getHandelssortimentsId(hsNamn);
                let handelssortimentIdn: number[] = [id];
                handelssortimentIdn = handelssortimentIdn.concat(this.getRelateradeHandelssortiment(id));
                this.produktionsmalPlanChartService.searchForArbetsorder(handelssortimentIdn, planeringsstatus, this.produktionsmalPlan);
            } else if (this.isHandelssortimentGruppPeriodsattStapel(mouseElement, planeringsstatus)) {
                const hsg = this.produktionsmalPlanChartService.getSelectedHandelssortimentGrupp(hsNamn, this.produktionsmalPlan.handelssortimentsgrupper);
                this.produktionsmalPlanChartService.searchForArbetsorder([], planeringsstatus, this.produktionsmalPlan, hsg.produktgruppIds);
            }
            return;
        }

        // I handelssortimentsgruppnivå men klickar till höger om namnen.
        if (mousePoint.y <= chart.scales.y.top || mousePoint.y >= chart.scales.y.bottom ||
            mousePoint.x >= chart.scales.y.right) {
            return;
        }
        // Om man är på handelssortimentnivå, men inte klickar på en stapel så ska man inte oavsiktligt klicka på en annan handelssortimentgrupp.
        if (this.chartMode === ChartMode.Handelssortimentsgrupper) {
            const selectedHandelssortimentsgrupp = this.produktionsmalPlan
                .handelssortimentsgrupper[chart.scales.y.getValueForPixel(mousePoint.y)];
            this.loadHandelssortimentsgrupp(selectedHandelssortimentsgrupp);
        }
    }

    private readonly getHandelssortimentsId = (hsNamn: string) => {
        const hsg = this.produktionsmalPlan.handelssortimentsgrupper.find(p => p.handelssortiment.some(pp => pp.namn === hsNamn));

        return hsg?.handelssortiment?.find(x => x.namn === hsNamn).id;
    };

    private getRelateradeHandelssortiment(handelssortimentId: number): number[] {
        const relateradeHandelssortimentIdn: number[] = [];

        if(this.handelssortimentsgrupper) {
            this.handelssortimentsgrupper.forEach(x => {
                x.children.forEach(y => {
                    if (y.relaterarTillHandelssortimentId === handelssortimentId) {
                        relateradeHandelssortimentIdn.push(y.id);
                    }
                });
            });
        } else {
            return [];
        }

        return relateradeHandelssortimentIdn;
    }

    private readonly markNegativeGoals = (labels): void => {
        for (let i = 0; i < labels.length; i++) {
            const label = labels[i];
            if (this.negativeGoals.indexOf(label) > -1) {
                labels[i] = `${this.warningGlyph} ${label}`;
            }
        }
    };
}
